import { useEffect, useMemo, useRef, useState } from 'react';
import type { ProductIndexEntry } from '@/shared/bfSeller';
import type { ProductProps } from '@/types';

export const useProductPagination = ({
  entries,
  page,
  pageSize,
  hydrate,
  cacheKey,
  enabled = true,
}: {
  entries: ProductIndexEntry[];
  page: number;
  pageSize: number;
  hydrate: (entries: ProductIndexEntry[]) => Promise<ProductProps[]>;
  cacheKey: string;
  enabled?: boolean;
}) => {
  const abertos = useRef(new Map<number, ProductProps>());
  const tentados = useRef(new Set<number>());
  const lastCacheKey = useRef(cacheKey);
  const hydrateRef = useRef(hydrate);
  hydrateRef.current = hydrate;
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const [items, setItems] = useState<ProductProps[]>([]);
  const [isLoadingPage, setIsLoadingPage] = useState(false);

  if (lastCacheKey.current !== cacheKey) {
    lastCacheKey.current = cacheKey;
    abertos.current = new Map();
    tentados.current = new Set();
  }

  const target = Math.max(1, page) * pageSize;
  const total = entries.length;
  const hasMore = items.length < total;

  const visibleSignature = useMemo(
    () => `${cacheKey}|${target}|${entries.length}|${entries[0]?.cod ?? ''}|${entries[entries.length - 1]?.cod ?? ''}`,
    [cacheKey, target, entries]
  );

  useEffect(() => {
    if (!enabled) {
      setItems([]);
      return;
    }

    let cancelled = false;
    const lista = entriesRef.current;
    const alvo = target;

    const codigo = (valor: unknown) => Number(valor);

    const publicar = () => {
      if (cancelled) return;
      const out: ProductProps[] = [];
      for (const entry of lista) {
        const produto = abertos.current.get(codigo(entry.cod));
        if (produto) out.push(produto);
        if (out.length >= alvo) break;
      }
      setItems(out);
    };

    const proximoLote = () => {
      const lote: ProductIndexEntry[] = [];
      for (const entry of lista) {
        const cod = codigo(entry.cod);
        if (abertos.current.has(cod) || tentados.current.has(cod)) continue;
        lote.push(entry);
        if (lote.length >= pageSize) break;
      }
      return lote;
    };

    const preencher = async () => {
      setIsLoadingPage(true);
      try {
        while (!cancelled) {
          let preenchidos = 0;
          for (const entry of lista) {
            if (abertos.current.has(codigo(entry.cod))) preenchidos += 1;
            if (preenchidos >= alvo) break;
          }
          if (preenchidos >= alvo) break;

          const lote = proximoLote();
          if (!lote.length) break;

          const produtos = await hydrateRef.current(lote);
          if (cancelled) return;
          for (const entry of lote) tentados.current.add(codigo(entry.cod));
          for (const produto of produtos) {
            const cod = codigo(produto.COD_PRODUTO);
            if (Number.isFinite(cod)) abertos.current.set(cod, produto);
          }
        }
      } catch {
      } finally {
        if (!cancelled) {
          publicar();
          setIsLoadingPage(false);
        }
      }
    };

    void preencher();

    return () => {
      cancelled = true;
    };
  }, [visibleSignature, enabled, pageSize, target]);

  return { items, total, hasMore, isLoadingPage };
};
